import { ThemeEvents } from '@theme/events';

class SodiiVolumePricing extends HTMLElement {
  #section;
  #scope;
  #currencyFormatter;
  #unitPrice = 0;

  connectedCallback() {
    this.#unitPrice = Number(this.dataset.unitPrice) || 0;
    this.#section = this.closest('.shopify-section, dialog');
    this.#scope = this.closest('[data-sodii-volume-scope], .product-details');
    this.#scope?.classList.add('has-sodii-volume-pricing');

    this.#currencyFormatter = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.dataset.currency || 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    this.addEventListener('change', this.#handleTierChange);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#scope?.addEventListener('sodii:variant-update', this.#handleSodiiVariantUpdate);

    this.#updatePrices();
    requestAnimationFrame(() => {
      this.#updateTierAvailability();
      this.#applySelectedTier();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleTierChange);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#scope?.removeEventListener('sodii:variant-update', this.#handleSodiiVariantUpdate);

    if (!this.#scope?.querySelector('sodii-volume-pricing')) {
      this.#scope?.classList.remove('has-sodii-volume-pricing');
    }
  }

  #handleTierChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-volume-tier]')) return;

    this.#applySelectedTier();
  };

  #handleVariantUpdate = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;

    const nextPrice = Number(event.detail?.resource?.price);
    const nextWidget = event.detail?.data?.html?.querySelector(
      `[data-sodii-volume-pricing-block-id="${CSS.escape(this.dataset.sodiiVolumePricingBlockId || '')}"]`
    );

    this.#unitPrice = nextPrice || Number(nextWidget?.dataset.unitPrice) || this.#unitPrice;
    this.dataset.unitPrice = this.#unitPrice.toString();
    this.#updatePrices();

    requestAnimationFrame(() => {
      this.#updateTierAvailability();
      this.#applySelectedTier();
    });
  };

  #handleSodiiVariantUpdate = (event) => {
    if (event.detail?.productId?.toString() !== this.dataset.productId) return;

    this.#unitPrice = Number(event.detail?.price) || this.#unitPrice;
    this.dataset.unitPrice = this.#unitPrice.toString();
    this.#updatePrices();
    this.#updateTierAvailability();
    this.#applySelectedTier();
  };

  #getQuantityInput() {
    return this.#scope?.querySelector('product-form-component input[name="quantity"]') || null;
  }

  #applySelectedTier() {
    const selectedTier = this.querySelector('[data-volume-tier]:checked');
    if (!(selectedTier instanceof HTMLInputElement) || selectedTier.disabled) return;

    const quantityInput = this.#getQuantityInput();
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const quantity = Number(selectedTier.dataset.quantity) || 1;
    const quantitySelector = quantityInput.closest('quantity-selector-component');

    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(quantity.toString());
      quantitySelector.onQuantityChange?.();
      quantitySelector.updateButtonStates?.();
      return;
    }

    quantityInput.value = quantity.toString();
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #updateTierAvailability() {
    const quantityInput = this.#getQuantityInput();
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const minimum = Number(quantityInput.min) || 1;
    const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
    const inCart = Number(quantityInput.dataset.cartQuantity) || 0;
    const effectiveMaximum = maximum === Infinity ? Infinity : Math.max(minimum, maximum - inCart);
    const tiers = Array.from(this.querySelectorAll('[data-volume-tier]'));

    for (const tier of tiers) {
      if (!(tier instanceof HTMLInputElement)) continue;

      const quantity = Number(tier.dataset.quantity) || 1;
      tier.disabled = quantity < minimum || quantity > effectiveMaximum;
    }

    const selectedTier = this.querySelector('[data-volume-tier]:checked');
    if (selectedTier instanceof HTMLInputElement && !selectedTier.disabled) return;

    const firstAvailableTier = tiers.find((tier) => tier instanceof HTMLInputElement && !tier.disabled);
    if (firstAvailableTier instanceof HTMLInputElement) firstAvailableTier.checked = true;
  }

  #updatePrices() {
    for (const tier of this.querySelectorAll('[data-volume-tier]')) {
      if (!(tier instanceof HTMLInputElement)) continue;

      const quantity = Number(tier.dataset.quantity) || 1;
      const discount = Number(tier.dataset.discount) || 0;
      const total = Math.round((this.#unitPrice * quantity * (100 - discount)) / 100);
      const each = total / quantity;
      const tierLabel = tier.closest('.sodii-volume-pricing__tier');

      const totalElement = tierLabel?.querySelector('[data-volume-total]');
      const eachElement = tierLabel?.querySelector('[data-volume-each]');

      if (totalElement) totalElement.textContent = this.#formatMoney(total);
      if (eachElement) eachElement.textContent = `${this.#formatMoney(each)}/${this.dataset.eachLabel || 'each'}`;
    }
  }

  #formatMoney(cents) {
    return this.#currencyFormatter.format(cents / 100);
  }

}

if (!customElements.get('sodii-volume-pricing')) {
  customElements.define('sodii-volume-pricing', SodiiVolumePricing);
}
